/**
 * Scroll animace pomocí GSAP + ScrollTrigger + SplitText.
 * Inicializuje se přes $(document).ready() s krátkým timeoutem
 * aby byl DOM plně vyrenderován před výpočtem pozic.
 *
 * Použití v HTML:
 *   data-animation="show-per-line"   → animace po řádcích textu (SplitText)
 *   data-animation="display"         → fade+slide animace elementu
 *   data-animation="scale"           → scale+fade animace
 *   data-parallax                    → parallax scrolling efekt
 *
 * Společné data atributy:
 *   data-direction="from-bottom|from-top|from-left|from-right"
 *   data-delay="0.2"         → zpoždění animace v sekundách
 *   data-start="top 80%"     → ScrollTrigger start pozice
 *   data-end="bottom 20%"    → ScrollTrigger end pozice
 */

/**
 * Animace textu po řádcích pomocí GSAP SplitText.
 * Rozdělí element na řádky a animuje je postupně (stagger).
 *
 * @returns {void}
 */
function initLineAnimations() {
    const $elements = $('[data-animation="show-per-line"]');

    $elements.each(function() {
        const el = this;
        const $el = $(el);

        const direction = $el.data('direction') || 'from-bottom';
        const start = $el.data('start') || "top 90%";
        const end = $el.data('end') || "bottom 10%";

        let y = 0, x = 0;
        const isSmallScreen = window.innerWidth <= 1000;
        switch (direction) {
            case 'from-bottom': y = 20; break;
            case 'from-top':    y = -20; break;
            case 'from-left':   x = isSmallScreen ? -10 : -20; break;
            case 'from-right':  x = isSmallScreen ? 10 : 20; break;
        }

        var fromVars = { y: y, x: x, opacity: 0 };
        var toVars = {
            y: 0,
            x: 0,
            opacity: 1,
            duration: 0.6,
            stagger: 0.2,
            ease: "none",
            scrollTrigger: {
                trigger: el,
                start: start,
                end: end,
                toggleActions: "play none none reverse",
                // markers: true,
            }
        };

        const split = new SplitText(el, {
            type: "lines",
            autoSplit: true,
            reduceWhiteSpace: false,
            onSplit: (self) => {
                return gsap.fromTo(self.lines, fromVars, toVars);
            }
        });

        gsap.fromTo(split.lines, fromVars, toVars);
    });
}

/**
 * Fade + slide animace jednotlivých elementů.
 * Automaticky detekuje elementy na stejném řádku a přidává staggered delay.
 *
 * @returns {void}
 */
function initDisplayAnimations() {
    const $elements = $('[data-animation="display"]');

    // Seskupíme prvky podle rodiče a spočítáme, kolik jich je na stejném řádku
    const groupedByParent = {};

    $elements.each(function() {
        const $el = $(this);
        const $parent = $el.parent();
        const parentId = $parent.data('animation-group') || $parent.get(0);

        if (!groupedByParent[parentId]) {
            groupedByParent[parentId] = [];
        }
        groupedByParent[parentId].push(this);
    });

    // Pro každou skupinu zjistíme, které prvky jsou na stejném řádku
    const rowIndexMap = new Map();

    Object.values(groupedByParent).forEach(group => {
        if (group.length <= 1) {
            rowIndexMap.set(group[0], 0);
            return;
        }

        // Seskupíme podle top pozice (s tolerancí 5px)
        const rowGroups = {};
        group.forEach(el => {
            const top = Math.round(el.getBoundingClientRect().top / 5) * 5;
            if (!rowGroups[top]) {
                rowGroups[top] = [];
            }
            rowGroups[top].push(el);
        });

        // Přiřadíme index v rámci řádku (0 pro prvky, které jsou samy na řádku)
        Object.values(rowGroups).forEach(rowElements => {
            rowElements.forEach((el, index) => {
                rowIndexMap.set(el, rowElements.length > 1 ? index : 0);
            });
        });
    });

    $elements.each(function() {
        const el = this;
        const $el = $(el);

        const direction = $el.data('direction') || 'from-bottom';
        const baseDelay = parseFloat($el.data('delay')) || 0;
        const delayStep = parseFloat($el.data('delay-step')) || 0.15;
        const start = $el.data('start') || "top 90%";
        const end = $el.data('end') || "bottom 10%";
        var trigger = el;
        if ($el.data("trigger")) {
            trigger = $el.closest($($el.data("trigger")));
        }

        const rowIndex = rowIndexMap.get(el) || 0;
        const calculatedDelay = baseDelay + (rowIndex * delayStep);

        let y = 0, x = 0;
        const isSmallScreen = window.innerWidth <= 1000;
        switch (direction) {
            case 'from-bottom': y = 20; break;
            case 'from-top':    y = -20; break;
            case 'from-left':   x = isSmallScreen ? -10 : -20; break;
            case 'from-right':  x = isSmallScreen ? 10 : 20; break;
        }

        const inViewport = el.getBoundingClientRect().top < window.innerHeight;

        var fromVars = { x: x, y: y, opacity: 0 };
        var toVars = {
            x: 0,
            y: 0,
            opacity: 1,
            duration: 0.6,
            delay: calculatedDelay,
            ease: "none",
            ...(!inViewport && {
                scrollTrigger: {
                    trigger: trigger,
                    start: start,
                    end: end,
                    toggleActions: "play none none reverse",
                    // markers: true,
                }
            })
        };

        gsap.fromTo(el, fromVars, toVars);
    });
}

/**
 * Scale + fade animace (zvětšení nebo zmenšení při vstupu do viewportu).
 *
 * @returns {void}
 */
function initScaleAnimations() {
    const $elements = $('[data-animation="scale"]');

    $elements.each(function() {
        const el = this;
        const $el = $(el);

        const direction = $el.data('direction') || 'from-big';
        const delay = $el.data('delay') || 0;
        const start = $el.data('start') || "top 90%";
        const end = $el.data('end') || "bottom 10%";

        let scale = 1;
        switch (direction) {
            case 'from-big':   scale = 1.05; break;
            case 'from-small': scale = 0.95; break;
        }

        var fromVars = { opacity: 0, scale: scale, x: -50, y: 0 };
        var toVars = {
            opacity: 1,
            scale: 1,
            x: 0,
            y: 0,
            duration: 0.6,
            delay: delay,
            ease: "none",
            scrollTrigger: {
                trigger: el,
                start: start,
                end: end,
                toggleActions: "play none none reverse",
                // markers: true,
            }
        };

        gsap.fromTo(el, fromVars, toVars);
    });
}

/**
 * Parallax scrolling efekt.
 * Pohybuje elementem při scrollování na základě data atributů.
 *
 * @returns {void} 
 */
function initParallaxAnimations() {
    const $elements = $('[data-parallax]');

    $elements.each(function() {
        const el = this;
        const $el = $(el);

        const speed = parseFloat($el.data('parallax-speed')) || 0.5;
        const direction = $el.data('parallax-direction') || 'down';
        const start = $el.data('parallax-start') || "top bottom";
        const end = $el.data('parallax-end') || "bottom top";
        const fromY = $el.data('parallax-from-y') !== undefined ? parseFloat($el.data('parallax-from-y')) : null;
        const fromX = $el.data('parallax-from-x') !== undefined ? parseFloat($el.data('parallax-from-x')) : null;

        let yPercent = 0;
        let xPercent = 0;

        switch (direction) {
            case 'down':  yPercent =  speed * 100; break;
            case 'up':    yPercent = -speed * 100; break;
            case 'right': xPercent =  speed * 100; break;
            case 'left':  xPercent = -speed * 100; break;
        }

        const fromConfig = {};
        if (fromY !== null) fromConfig.yPercent = fromY;
        if (fromX !== null) fromConfig.xPercent = fromX;

        if (Object.keys(fromConfig).length > 0) {
            gsap.set(el, fromConfig);
        }

        gsap.to(el, {
            yPercent: yPercent,
            xPercent: xPercent,
            ease: "none",
            scrollTrigger: {
                trigger: el,
                start: start,
                end: end,
                scrub: true,
                // markers: true,
            }
        });
    });
}

/**
 * Count-up animace čísel při vstupu do viewportu.
 * Hledá elementy `.number__number p`, extrahuje číslo + jednotku
 * a animuje od startovní do finální hodnoty.
 *
 * @returns {void}
 */
function initCountUpAnimations() {
    const $elements = $('.number__number p');

    $elements.each(function() {
        const el = this;
        const $el = $(el);

        let $existingUnitSpan = $el.find('.counter-unit').first();
        let unitHTML = null;
        if ($existingUnitSpan.length > 0) {
            unitHTML = $existingUnitSpan.html();
        }

        const finalText = $el.text().trim();
        const numberMatch = finalText.match(/^([\d\s,.]+)(.*)$/);
        if (!numberMatch) return;

        const numberPart = numberMatch[1].trim();
        const unit = numberMatch[2].trim();
        const finalValue = parseFloat(numberPart.replace(/\s/g, '').replace(',', '.'));

        if (isNaN(finalValue)) return;

        const hasDecimals = numberPart.includes(',') || numberPart.includes('.');
        const decimalPlaces = hasDecimals ? 1 : 0;

        let startValue;
        if (finalValue <= 10)       startValue = Math.max(0, finalValue - 2);
        else if (finalValue <= 100) startValue = finalValue - 5;
        else                        startValue = finalValue - 10;

        const $numberSpan = $('<span class="counter-number"></span>');
        let $unitSpan;
        if (unitHTML !== null) {
            $unitSpan = $('<span class="counter-unit"></span>').html(unitHTML);
        } else if (unit) {
            $unitSpan = $('<span class="counter-unit"> ' + unit + '</span>');
        }

        $el.empty();
        $el.append($numberSpan);
        if ($unitSpan) $el.append($unitSpan);

        const counter = { value: startValue };
        $numberSpan.text(startValue.toFixed(decimalPlaces).replace('.', ','));

        ScrollTrigger.create({
            trigger: el,
            start: "top 80%",
            once: true,
            onEnter: function() {
                gsap.to(counter, {
                    value: finalValue,
                    duration: 1.5,
                    ease: "power2.out",
                    onUpdate: function() {
                        $numberSpan.text(counter.value.toFixed(decimalPlaces).replace('.', ','));
                    },
                    onComplete: function() {
                        $numberSpan.text(numberPart);
                    }
                });
            }
        });
    });
}

/**
 * Animace hlavičky – sjede z hora dolů při načtení stránky.
 *
 * @returns {void}
 */
function initHeaderAnimation() {
    const el = document.querySelector('header.site-header-v2');
    if (!el) return;

    gsap.to(el, {
        autoAlpha: 1,
        y: 0,
        duration: 0.7,
        ease: "power2.out",
    });
}

/**
 * Inicializace – registrace GSAP pluginů a spuštění animací.
 */
$(document).ready(function () {
    gsap.registerPlugin(ScrollTrigger, SplitText);

    // Schovat header ihned – animace ho zobrazí po setTimeout
    gsap.set('header.site-header-v2', { autoAlpha: 0, y: '-100%' });

    // Integrace Lenis s GSAP ScrollTrigger – synchronizuje scroll eventy
    if (window.lenis && typeof window.lenis.on === 'function') {
        window.lenis.on('scroll', function() { ScrollTrigger.update(); });
        gsap.ticker.add(function(t) { window.lenis.raf(t * 1000); });
        gsap.ticker.lagSmoothing(0);
    }

    setTimeout(() => {
        initHeaderAnimation();
        initLineAnimations();
        initDisplayAnimations();
        initScaleAnimations();
        initParallaxAnimations();
        initCountUpAnimations();
    }, 300);

});
